import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import './TimeFilter.css';

interface TimeProps {
  disabled?: boolean;
}

interface TimePeriod {
  label: string;
  hours: number[];
  icon: string;
}

const TimeFilter: React.FC<TimeProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const { tempFilters, setTimeFilter, activeHours, applyTempFilters, cancelTempFilters, hasUnappliedChanges } = useFilters();
  const { isLaptop, isiPad, isiPhone } = useResponsive();
  const [showPicker, setShowPicker] = useState(false);
  
  const time = tempFilters.time; 

  const formatHour = (hour: number) => {
    if (hour === 0) return '12AM';
    if (hour === 12) return '12PM';
    if (hour < 12) return `${hour}AM`;
    return `${hour - 12}PM`;
  };

  const getAvailableTimePeriods = (): Record<string, TimePeriod> => {
    
    const morning = activeHours.filter(h => h >= 5 && h <= 10);
    const lunch = activeHours.filter(h => h >= 11 && h <= 15);
    const dinner = activeHours.filter(h => h >= 16 && h <= 20);
    const lateNight = activeHours.filter(h => h >= 21 || h <= 4);

    const periods: Record<string, TimePeriod> = {};
    
    if (morning.length > 0) {
      periods.morning = { label: t('timeFilter.morning'), hours: morning, icon: '🌅' };
    }
    if (lunch.length > 0) {
      periods.lunch = { label: t('timeFilter.lunch'), hours: lunch, icon: '☀️' };
    }
    if (dinner.length > 0) {
      periods.dinner = { label: t('timeFilter.dinner'), hours: dinner, icon: '🌆' };
    }
    if (lateNight.length > 0) {
      periods.lateNight = { label: t('timeFilter.lateNight'), hours: lateNight, icon: '🌙' };
    }
    
    return periods;
  };

  const availableTimePeriods = getAvailableTimePeriods();

  const handleTimePeriodToggle = (periodKey: string) => {
    if (disabled) return; 
    
    const period = availableTimePeriods[periodKey];
    if (!period) return;

    const isCurrentlySelected = period.hours.every((hour: number) => time.selectedHours.includes(hour));
    const periodHours = period.hours;
    
    let newSelectedHours = [...time.selectedHours];
    
    if (isCurrentlySelected) {
      newSelectedHours = newSelectedHours.filter((hour: number) => !periodHours.includes(hour));
    } else {
      periodHours.forEach((hour: number) => {
        if (!newSelectedHours.includes(hour)) {
          newSelectedHours.push(hour);
        }
      });
    }

    newSelectedHours = newSelectedHours.filter((hour: number) => activeHours.includes(hour));

    const newTimePeriods: Record<string, boolean> = {};
    Object.keys(availableTimePeriods).forEach(key => {
      newTimePeriods[key] = availableTimePeriods[key].hours.every((h: number) => newSelectedHours.includes(h));
    });

    setTimeFilter({
      selectedHours: newSelectedHours.sort((a, b) => a - b),
      allHours: newSelectedHours.length === activeHours.length,
      timePeriods: newTimePeriods
    });
  };

  const handleHourToggle = (hour: number) => {
    if (disabled) return; 
    
    if (!activeHours.includes(hour)) return;

    const newSelectedHours = time.selectedHours.includes(hour)
      ? time.selectedHours.filter(h => h !== hour)
      : [...time.selectedHours, hour].sort((a, b) => a - b);

    const newTimePeriods: Record<string, boolean> = {};
    Object.keys(availableTimePeriods).forEach(key => {
      newTimePeriods[key] = availableTimePeriods[key].hours.every((h: number) => newSelectedHours.includes(h));
    });

    setTimeFilter({
      selectedHours: newSelectedHours,
      allHours: newSelectedHours.length === activeHours.length,
      timePeriods: newTimePeriods
    });
  };

  const handleAllHoursToggle = () => {
    if (disabled) return; 
    
    if (time.allHours) {
      const newTimePeriods: Record<string, boolean> = {};
      Object.keys(availableTimePeriods).forEach(key => {
        newTimePeriods[key] = false;
      });

      setTimeFilter({
        selectedHours: [],
        allHours: false,
        timePeriods: newTimePeriods
      });
    } else {
      const newTimePeriods: Record<string, boolean> = {};
      Object.keys(availableTimePeriods).forEach(key => {
        newTimePeriods[key] = true;
      });

      setTimeFilter({
        selectedHours: [...activeHours],
        allHours: true,
        timePeriods: newTimePeriods
      });
    }
  };

  const getButtonText = () => {
    if (time.allHours) {
      return t('timeFilter.buttonText.allTime');
    } else if (time.selectedHours.length === 0) {
      return t('timeFilter.buttonText.selectTime');
    } else {
      const activePeriodLabels: string[] = [];
      Object.keys(availableTimePeriods).forEach(key => {
        if (time.timePeriods[key] === true) {
          const label = availableTimePeriods[key].label.split(' (')[0];
          activePeriodLabels.push(label);
        }
      });
      
      if (activePeriodLabels.length > 0) {
        return activePeriodLabels.length <= 2 
          ? activePeriodLabels.join(', ')
          : `${activePeriodLabels.slice(0, 2).join(', ')} 외`;
      } else {
        return t('timeFilter.buttonText.hoursSelected', { count: time.selectedHours.length });
      }
    }
  };

  const timeTextStyles = {
    fontSize: isiPhone ? '0.85rem' : isiPad ? '0.9rem' : isLaptop ? '1.05rem' : '1.2rem',
    fontWeight: 700,
    color: 'white',
    letterSpacing: '0.3px',
    flex: 1,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  };

  return (
    <div className={`time-filter ${disabled ? 'disabled' : ''}`}>
      <button 
        className={`filter-button time-filter-button ${time.selectedHours.length > 0 ? 'active' : ''}`}
        onClick={() => !disabled && setShowPicker(!showPicker)}
        disabled={disabled}
      >
        <span className="time-icon">🕒</span>
        <span className="time-text" style={timeTextStyles}>{getButtonText()}</span>
        <span className="dropdown-arrow">{showPicker ? '▲' : '▼'}</span>
      </button>

      {showPicker && !disabled && ( 
        <div className="time-picker-dropdown">
          <div className="quick-select-section">
            <div className="quick-select-title">{t('timeFilter.timePeriods')}</div>
            <div className="time-period-grid">
              {Object.entries(availableTimePeriods).map(([key, period]) => (
                <button
                  key={key}
                  className={`time-period-button ${time.timePeriods[key] === true ? 'active' : ''}`}
                  onClick={() => handleTimePeriodToggle(key)}
                >
                  <span className="period-icon">{period.icon}</span>
                  <span className="period-label">{period.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="hour-selection-section">
            <div className="hour-selection-title">{t('timeFilter.individualHours')}</div>
            <div className="hour-buttons">
              {activeHours.map(hour => (
                <button
                  key={hour}
                  className={`hour-button ${time.selectedHours.includes(hour) ? 'selected' : ''}`}
                  onClick={() => handleHourToggle(hour)}
                >
                  {formatHour(hour)}
                </button>
              ))}
            </div>
            
            {activeHours.length < 24 && (
              <div className="operating-hours-note">
                <small>{t('timeFilter.dataHoursNote', { start: formatHour(activeHours[0]), end: formatHour(activeHours[activeHours.length - 1]) })}</small>
              </div>
            )}
          </div>
          
          <div className="picker-actions">
            <button 
              className="cancel-button"
              onClick={() => {
                cancelTempFilters();
                setShowPicker(false);
              }}
              disabled={!hasUnappliedChanges}
            >
              {t('timeFilter.cancel')}
            </button>
            <button 
              className="apply-button"
              onClick={() => {
                applyTempFilters();
                setShowPicker(false);
              }}
            >
              {t('timeFilter.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeFilter;